import styles from "./Communication.module.scss";
import classNames from "classnames/bind";
import { PlusIcon, FilterIcon, UserIcon } from "~/assets/icons";
import { useState, useRef, useEffect } from "react";
import PrimaryButton from "~/components/common/primary-button/PrimaryButton";
import { ArchiveIcon, PresentationIcon } from "lucide-react";
import PrimaryInput from "~/components/common/primary-input/PrimaryInput";
import AddNewMessage from "./add-new-message/AddNewMessage";
import { useAppDispatch, useAppSelector } from "~/redux/hooks";
import {
  handleGetSmsConversations,
  handleGetSmsMessages,
  handleSendSms,
  handleUpdateSmsConversation,
  handleGetSmsContactDetails,
} from "~/thunks/sms/smsThunk";
import { smsActions } from "~/thunks/sms/smsSlice";
import { notify } from "~/utils/helper";

const cx = classNames.bind(styles);

const Communication = () => {
  const dispatch = useAppDispatch();
  const {
    conversations,
    messages,
    selectedConversation,
    conversationsLoading,
    messagesLoading,
    contactDetails,
    contactDetailsLoading,
    needRefreshConversations,
    needRefreshMessages,
    filterParams,
  } = useAppSelector((state) => state.sms);

  const [selectedTab, setSelectedTab] = useState<"active" | "archived">("active");
  const [selectedConversationIndex, setSelectedConversationIndex] = useState(0);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filterValues, setFilterValues] = useState({
    assignedToOthers: "",
    salesRep: "",
    engageNumber: "",
  });
  const [messageInput, setMessageInput] = useState("");
  const filterButtonRef = useRef<HTMLDivElement>(null);
  const [isComposeModalOpen, setIsComposeModalOpen] = useState(false);

  // Fetch conversations when tab or filters change
  useEffect(() => {
    const { status: _, ...otherFilters } = filterParams;
    dispatch(
      handleGetSmsConversations({
        params: {
          status: selectedTab,
          ...otherFilters,
          page: 1,
          limit: 20,
        },
      })
    );
  }, [dispatch, selectedTab, filterParams, needRefreshConversations]);

  // Fetch messages when conversation is selected
  useEffect(() => {
    if (selectedConversation) {
      dispatch(
        handleGetSmsMessages({
          conversationId: selectedConversation.id,
          params: { page: 1, limit: 50 },
        })
      );
    }
  }, [dispatch, selectedConversation, needRefreshMessages]);

  // Fetch contact details when conversation is selected
  useEffect(() => {
    if (selectedConversation) {
      dispatch(
        handleGetSmsContactDetails({
          conversationId: selectedConversation.id,
        })
      );
    }
  }, [dispatch, selectedConversation]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        filterButtonRef.current &&
        !filterButtonRef.current.contains(event.target as Node)
      ) {
        setIsFilterOpen(false);
      }
    };

    if (isFilterOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isFilterOpen]);

  // Handler functions
  const handleApplyFilter = () => {
    dispatch(
      smsActions.setFilterParams({
        assignedTo: filterValues.assignedToOthers,
        salesRep: filterValues.salesRep,
        search: filterValues.engageNumber,
      })
    );
    setIsFilterOpen(false);
  };

  const handleArchiveConversation = async (conversationId: string) => {
    try {
      await dispatch(
        handleUpdateSmsConversation({
          conversationId,
          data: { status: "archived" },
        })
      ).unwrap();
      notify("success", "Conversation archived");
    } catch (error) {
      notify("error", "Failed to archive conversation");
    }
  };

  const handleMarkUnread = async () => {
    if (!selectedConversation) return;
    try {
      await dispatch(
        handleUpdateSmsConversation({
          conversationId: selectedConversation.id,
          data: { unreadCount: 1 },
        })
      ).unwrap();
      notify("success", "Marked as unread");
    } catch (error) {
      notify("error", "Failed to mark as unread");
    }
  };

  const handleSendMessage = async () => {
    if (!messageInput.trim() || !selectedConversation) return;

    try {
      await dispatch(
        handleSendSms({
          to: selectedConversation.phoneNumber,
          body: messageInput.trim(),
          conversationId: selectedConversation.id,
        })
      ).unwrap();

      setMessageInput("");
      notify("success", "Message sent");
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Failed to send message";
      notify("error", errorMessage);
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  return (
    <div className={cx("communication-container")}>
      <div className={cx("body")}>
        {/* Left Column - Conversations List */}
        <div className={cx("conversations-panel")}>
          <div className={cx("conversations-header")}>
            <h2 className={cx("conversations-title")}>Conversations</h2>
            <div className={cx("header-actions")}>
              <div
                ref={filterButtonRef}
                className={cx("filter-button-wrapper")}
              >
                <PrimaryButton
                  onClick={() => setIsFilterOpen(!isFilterOpen)}
                  className={cx("filter-button")}
                  icon={<FilterIcon className={cx("filter-icon")} />}
                />

                {isFilterOpen && (
                  <>
                    <div
                      className={cx("filter-dropdown-backdrop")}
                      onClick={() => setIsFilterOpen(false)}
                    />
                    <div className={cx("filter-dropdown")}>
                      <div className={cx("filter-dropdown-header")}>
                        <h3 className={cx("filter-dropdown-title")}>Filter</h3>
                        <button
                          className={cx("clear-filters-button")}
                          onClick={() => {
                            setFilterValues({
                              assignedToOthers: "",
                              salesRep: "",
                              engageNumber: "",
                            });
                          }}
                        >
                          Clear Filters
                        </button>
                      </div>

                      <div className={cx("filter-dropdown-content")}>
                        <div className={cx("filter-dropdown-content-item")}>
                          <span
                            className={cx("filter-dropdown-content-item-label")}
                          >
                            Assigned to others
                          </span>
                          <div className={cx("filter-input-wrapper")}>
                            <UserIcon className={cx("filter-input-icon")} />
                            <input
                              type="text"
                              placeholder="Assigned to others"
                              value={filterValues.assignedToOthers}
                              onChange={(e) =>
                                setFilterValues({
                                  ...filterValues,
                                  assignedToOthers: e.target.value,
                                })
                              }
                              className={cx("filter-input")}
                            />
                          </div>
                        </div>

                        <div className={cx("filter-dropdown-content-item")}>
                          <span
                            className={cx("filter-dropdown-content-item-label")}
                          >
                            Sales Rep
                          </span>
                          <div className={cx("filter-input-wrapper")}>
                            <UserIcon className={cx("filter-input-icon")} />
                            <input
                              type="text"
                              placeholder="Sales Rep"
                              value={filterValues.salesRep}
                              onChange={(e) =>
                                setFilterValues({
                                  ...filterValues,
                                  salesRep: e.target.value,
                                })
                              }
                              className={cx("filter-input")}
                            />
                          </div>
                        </div>

                        <div className={cx("filter-dropdown-content-item")}>
                          <span
                            className={cx("filter-dropdown-content-item-label")}
                          >
                            Engage number
                          </span>
                          <div className={cx("filter-input-wrapper")}>
                            <span className={cx("filter-hash-icon")}>#</span>
                            <input
                              type="text"
                              placeholder="Engage number"
                              value={filterValues.engageNumber}
                              onChange={(e) =>
                                setFilterValues({
                                  ...filterValues,
                                  engageNumber: e.target.value,
                                })
                              }
                              className={cx("filter-input")}
                            />
                          </div>
                        </div>
                      </div>

                      <div className={cx("filter-dropdown-footer")}>
                        <PrimaryButton
                          title="Cancel"
                          onClick={() => setIsFilterOpen(false)}
                          className={cx("filter-cancel-button")}
                        />
                        <PrimaryButton
                          title="Apply"
                          onClick={handleApplyFilter}
                          className={cx("filter-apply-button")}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>

              <PrimaryButton
                onClick={() => setIsComposeModalOpen(true)}
                className={cx("add-button")}
                icon={<PlusIcon className={cx("plus-icon")} />}
              />
            </div>
          </div>

          <div className={cx("tabs-container")}>
            <button
              className={cx("tab", { active: selectedTab === "active" })}
              onClick={() => setSelectedTab("active")}
            >
              Inbox
            </button>
            <button
              className={cx("tab", { active: selectedTab === "archived" })}
              onClick={() => setSelectedTab("archived")}
            >
              Archived
            </button>
          </div>

          <div className={cx("conversations-list")}>
            {conversationsLoading ? (
              <div className={cx("loading-state")}>Loading conversations...</div>
            ) : conversations.length === 0 ? (
              <div className={cx("empty-state")}>No conversations found</div>
            ) : (
              conversations.map((conversation) => (
                <div
                  key={conversation.id}
                  className={cx("conversation-item", {
                    active: selectedConversation?.id === conversation.id,
                  })}
                  onClick={() => dispatch(smsActions.setSelectedConversation(conversation))}
                >
                  <div className={cx("conversation-header-row")}>
                    <span className={cx("conversation-name")}>
                      {conversation.contactName || conversation.phoneNumber}
                    </span>

                    <div className={cx("conversation-time-row")}>
                      <span className={cx("conversation-time")}>
                        {formatTime(conversation.lastMessageAt)}
                      </span>

                      {conversation.unreadCount > 0 && (
                        <span className={cx("unread-dot")}></span>
                      )}
                    </div>
                  </div>
                  <p className={cx("conversation-preview")}>
                    {conversation.lastMessage}
                  </p>
                  <div className={cx("conversation-actions")}>
                    <PrimaryButton
                      title="Archive"
                      onClick={() => handleArchiveConversation(conversation.id)}
                      className={cx("conversation-button", "archive-button")}
                      icon={<ArchiveIcon className={cx("conversation-icon")} />}
                    />
                    <PrimaryButton
                      title="Assign"
                      onClick={() => {}}
                      className={cx("conversation-button", "assign-button")}
                      icon={
                        <PresentationIcon className={cx("conversation-icon")} />
                      }
                    />
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Center Column - Chat Window */}
        <div className={cx("chat-panel")}>
          {selectedConversation ? (
            <>
              <div className={cx("chat-header")}>
                <button
                  className={cx("mark-unread-button")}
                  onClick={handleMarkUnread}
                >
                  Mark Unread
                </button>
                <h3 className={cx("chat-contact-name")}>
                  {selectedConversation.contactName || selectedConversation.phoneNumber}
                </h3>
                <span className={cx("chat-last-active")}>
                  Last message: {formatTime(selectedConversation.lastMessageAt)}
                </span>
              </div>

              <div className={cx("messages-container")}>
                {messagesLoading ? (
                  <div className={cx("loading-state")}>Loading messages...</div>
                ) : messages.length === 0 ? (
                  <div className={cx("empty-state")}>No messages yet</div>
                ) : (
                  messages.map((msg) => (
                    <div
                      key={msg.id}
                      className={cx("message", { outgoing: msg.direction === "outbound" })}
                    >
                      <div className={cx("message-content")}>
                        {msg.direction === "inbound" && (
                          <p className={cx("message-sender")}>
                            {selectedConversation.contactName || selectedConversation.phoneNumber}
                          </p>
                        )}
                        <p className={cx("message-text")}>{msg.body}</p>
                        <span className={cx("message-time")}>{formatTime(msg.sentAt)}</span>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className={cx("chat-input-container")}>
                <PrimaryInput
                  placeholder="Type your reply here"
                  name="message"
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  className={cx("chat-input")}
                />
                <PrimaryButton
                  title="Send"
                  onClick={handleSendMessage}
                  className={cx("send-button")}
                  disabled={!messageInput.trim()}
                />
              </div>
            </>
          ) : (
            <div className={cx("no-conversation-selected")}>
              Select a conversation to view messages
            </div>
          )}
        </div>

        {/* Right Column - Contact Details */}
        <div className={cx("contact-panel")}>
          {selectedConversation ? (
            <>
              <div className={cx("contact-header")}>
                <h3 className={cx("contact-name")}>
                  {contactDetails?.name || selectedConversation.contactName || "Unknown"}
                </h3>
              </div>
              <div className={cx("contact-details")}>
                {contactDetailsLoading ? (
                  <div className={cx("loading-state")}>Loading details...</div>
                ) : (
                  <>
                    <div className={cx("detail-item")}>
                      <div className={cx("detail-label-container")}>
                        <span className={cx("detail-label")}>Phone</span>
                        <span className={cx("detail-label-separator")}>:</span>
                      </div>
                      <span className={cx("detail-value")}>
                        {contactDetails?.phone || selectedConversation.phoneNumber}
                      </span>
                    </div>
                    <div className={cx("detail-item")}>
                      <div className={cx("detail-label-container")}>
                        <span className={cx("detail-label")}>Email</span>
                        <span className={cx("detail-label-separator")}>:</span>
                      </div>
                      <span className={cx("detail-value")}>
                        {contactDetails?.email || "N/A"}
                      </span>
                    </div>
                    <div className={cx("detail-item")}>
                      <div className={cx("detail-label-container")}>
                        <span className={cx("detail-label")}>Address</span>
                        <span className={cx("detail-label-separator")}>:</span>
                      </div>
                      <span className={cx("detail-value")}>
                        {contactDetails?.address || "N/A"}
                      </span>
                    </div>
                    <div className={cx("detail-item")}>
                      <div className={cx("detail-label-container")}>
                        <span className={cx("detail-label")}>Assigned</span>
                        <span className={cx("detail-label-separator")}>:</span>
                      </div>
                      <span className={cx("detail-value")}>
                        {contactDetails?.assignedTo?.name || "Unassigned"}
                      </span>
                    </div>
                    <div className={cx("detail-item")}>
                      <div className={cx("detail-label-container")}>
                        <span className={cx("detail-label")}>Created</span>
                        <span className={cx("detail-label-separator")}>:</span>
                      </div>
                      <span className={cx("detail-value")}>
                        {contactDetails?.createdAt
                          ? formatDateTime(contactDetails.createdAt)
                          : "N/A"}
                      </span>
                    </div>
                    <div className={cx("detail-item")}>
                      <div className={cx("detail-label-container")}>
                        <span className={cx("detail-label")}>
                          Opt In / Opt Out Status
                        </span>
                        <span className={cx("detail-label-separator")}>:</span>
                      </div>

                      <div className={cx("opt-status-button-container")}>
                        <PrimaryButton
                          title={
                            contactDetails?.optInStatus === "opted_in"
                              ? "Opted In"
                              : "Opted Out"
                          }
                          onClick={() => {}}
                          className={cx("opt-status-button")}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </>
          ) : (
            <div className={cx("no-contact-selected")}>
              Select a conversation to view details
            </div>
          )}
        </div>
      </div>

      <AddNewMessage
        open={isComposeModalOpen}
        onClose={() => setIsComposeModalOpen(false)}
      />
    </div>
  );
};

export default Communication;
